/**
 * Courier money — what Shiprocket bookings cost the platform, and what still
 * does not add up.
 *
 * Estimates are recorded when a cost is incurred; the actual amounts come from
 * the Shiprocket wallet passbook imported here. No balance on this screen is
 * live: the wallet figure is the last one a passbook stated, with its date.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import type { CourierExceptions, PassbookRow, ShipmentCharge } from '@terra/api-client';
import { Badge, Spinner } from '@terra/ui';
import { formatINR } from '@terra/utils';

import { api } from '../lib/api';

const inr = (paise: number | null | undefined) =>
  paise === null || paise === undefined ? '—' : formatINR(paise);

const errorText = (err: unknown) =>
  (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? (err instanceof Error ? err.message : 'Something went wrong');

const btn =
  'border-border/60 bg-surface-subtle text-ink-muted hover:text-ink rounded-lg border px-2 py-1 text-[11px] font-bold transition-colors disabled:opacity-50';
const primaryBtn =
  'rounded-lg bg-[#a8161b] px-3 py-1.5 text-[12px] font-bold text-white transition-colors hover:bg-[#850c10] disabled:opacity-50';

function Section(props: {
  title: string;
  hint?: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div>
          <h4 className="text-ink text-[14px] font-bold">
            {props.title}
            {props.count !== undefined && (
              <span className="text-ink-subtle ml-2 text-[12px] font-semibold">
                ({props.count})
              </span>
            )}
          </h4>
          {props.hint && <p className="text-ink-subtle mt-0.5 text-[12px]">{props.hint}</p>}
        </div>
      </div>
      {props.children}
    </section>
  );
}

function ChargeTable({ rows }: { rows: ShipmentCharge[] }) {
  if (rows.length === 0) return <p className="text-ink-subtle text-[12px]">Nothing here.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[12px]">
        <thead className="text-ink-subtle">
          <tr>
            <th className="py-1 pr-3">AWB</th>
            <th className="py-1 pr-3">Type</th>
            <th className="py-1 pr-3">Bearer</th>
            <th className="py-1 pr-3 text-right">Estimate</th>
            <th className="py-1 pr-3 text-right">Actual</th>
            <th className="py-1 pr-3">Since</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-border/40 border-t">
              <td className="py-1.5 pr-3 font-mono">{c.awbNumber ?? '—'}</td>
              <td className="py-1.5 pr-3">{c.type.replace(/_/g, ' ').toLowerCase()}</td>
              <td className="py-1.5 pr-3">{c.bearer.toLowerCase()}</td>
              <td className="py-1.5 pr-3 text-right">{inr(c.estimatedPaise)}</td>
              <td className="py-1.5 pr-3 text-right">{inr(c.actualPaise)}</td>
              <td className="py-1.5 pr-3">{new Date(c.occurredAt).toLocaleDateString('en-IN')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── CSV ──────────────────────────────────────────────────────────────────────

/** RFC-4180-ish: quoted fields, doubled quotes, commas and newlines inside quotes. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      if (row.some((f) => f.trim() !== '')) rows.push(row);
      row = [];
      field = '';
    } else field += ch;
  }
  row.push(field);
  if (row.some((f) => f.trim() !== '')) rows.push(row);
  return rows;
}

/** "₹1,234.50", "-80", "80.00 Dr" → paise. NaN when unreadable. */
function rupeesToPaise(raw: string): number {
  const cleaned = raw.replace(/[₹,\s]/g, '').replace(/(dr|cr)$/i, '');
  if (cleaned === '') return NaN;
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
}

interface Mapping {
  date: string;
  awb: string;
  description: string;
  ref: string;
  balance: string;
  /** Either one signed amount column (with an optional Dr/Cr type column)… */
  amount: string;
  type: string;
  /** …or separate debit and credit columns. */
  debit: string;
  credit: string;
}

const EMPTY_MAPPING: Mapping = {
  date: '',
  awb: '',
  description: '',
  ref: '',
  balance: '',
  amount: '',
  type: '',
  debit: '',
  credit: '',
};

/** Pick likely columns from a Shiprocket passbook header; the admin can change any. */
function guessMapping(headers: string[]): Mapping {
  const find = (...needles: RegExp[]) =>
    headers.find((h) => needles.some((n) => n.test(h.toLowerCase()))) ?? '';
  return {
    date: find(/date|time/),
    awb: find(/awb/),
    description: find(/description|remark|narration|particular/),
    ref: find(/transaction id|txn id|reference|ref/),
    balance: find(/balance/),
    amount: find(/^amount$|amount \(/),
    type: find(/^type$|dr\/cr|transaction type/),
    debit: find(/debit/),
    credit: find(/credit/),
  };
}

function toRows(table: string[][], m: Mapping): { rows: PassbookRow[]; skipped: number[] } {
  const [header, ...body] = table;
  const col = (name: string) => (name ? header!.indexOf(name) : -1);
  const idx = Object.fromEntries(
    (Object.keys(m) as Array<keyof Mapping>).map((k) => [k, col(m[k])]),
  ) as Record<keyof Mapping, number>;
  const rows: PassbookRow[] = [];
  const skipped: number[] = [];
  body.forEach((r, i) => {
    const get = (k: keyof Mapping) => (idx[k] >= 0 ? (r[idx[k]] ?? '').trim() : '');
    let amount = NaN;
    if (idx.debit >= 0 || idx.credit >= 0) {
      const d = rupeesToPaise(get('debit'));
      const c = rupeesToPaise(get('credit'));
      amount = (Number.isFinite(c) ? Math.abs(c) : 0) - (Number.isFinite(d) ? Math.abs(d) : 0);
    } else if (idx.amount >= 0) {
      amount = rupeesToPaise(get('amount'));
      const t = `${get('type')} ${get('amount')}`.toLowerCase();
      if (/\bdr\b|debit/.test(t)) amount = -Math.abs(amount);
      else if (/\bcr\b|credit/.test(t)) amount = Math.abs(amount);
    }
    const date = get('date');
    if (!Number.isFinite(amount) || amount === 0 || Number.isNaN(Date.parse(date))) {
      skipped.push(i + 2);
      return;
    }
    const balance = rupeesToPaise(get('balance'));
    rows.push({
      occurredAt: new Date(date).toISOString(),
      amountPaise: amount,
      ...(get('ref') ? { ref: get('ref') } : {}),
      ...(get('awb') ? { awbNumber: get('awb') } : {}),
      ...(get('description') ? { description: get('description') } : {}),
      ...(Number.isFinite(balance) ? { balanceAfterPaise: balance } : {}),
    });
  });
  return { rows, skipped };
}

function PassbookImport({ onDone }: { onDone: () => void }) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [table, setTable] = useState<string[][] | null>(null);
  const [mapping, setMapping] = useState<Mapping>(EMPTY_MAPPING);
  const parsed = useMemo(() => (table ? toRows(table, mapping) : null), [table, mapping]);
  const importMutation = useMutation({
    mutationFn: () =>
      api.courierFinance.importPassbook({
        ...(fileName ? { fileName } : {}),
        rows: parsed!.rows,
      }),
    onSuccess: () => onDone(),
  });

  const headers = table?.[0] ?? [];
  const select = (key: keyof Mapping, label: string) => (
    <label className="text-ink-muted flex flex-col gap-1 text-[11px] font-bold">
      {label}
      <select
        value={mapping[key]}
        onChange={(e) => setMapping({ ...mapping, [key]: e.target.value })}
        className="border-border/60 bg-surface rounded-lg border px-2 py-1 text-[12px] font-medium"
      >
        <option value="">—</option>
        {headers.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-3">
      <input
        type="file"
        accept=".csv,text/csv"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const t = parseCsv(await file.text());
          setFileName(file.name);
          setTable(t);
          setMapping(guessMapping(t[0] ?? []));
          importMutation.reset();
        }}
        className="text-[12px]"
      />
      {table && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {select('date', 'Date')}
            {select('amount', 'Amount (signed)')}
            {select('type', 'Dr/Cr column')}
            {select('debit', 'Debit')}
            {select('credit', 'Credit')}
            {select('awb', 'AWB')}
            {select('description', 'Description')}
            {select('ref', 'Transaction id')}
            {select('balance', 'Balance after')}
          </div>
          <p className="text-ink-subtle text-[12px]">
            {parsed!.rows.length} line{parsed!.rows.length === 1 ? '' : 's'} ready
            {parsed!.skipped.length > 0 &&
              ` · ${parsed!.skipped.length} skipped (rows ${parsed!.skipped.slice(0, 8).join(', ')}${parsed!.skipped.length > 8 ? '…' : ''}) — no readable date or amount`}
            . Lines already imported are recognised and skipped by the server.
          </p>
          <button
            type="button"
            className={primaryBtn}
            disabled={parsed!.rows.length === 0 || importMutation.isPending}
            onClick={() => importMutation.mutate()}
          >
            {importMutation.isPending ? 'Importing…' : 'Import passbook'}
          </button>
        </>
      )}
      {importMutation.data && (
        <div className="rounded-lg bg-emerald-50 p-3 text-[12px] text-emerald-800">
          Imported {importMutation.data.imported} line(s), {importMutation.data.duplicates} already
          on file. Matched {importMutation.data.matched} to shipments,{' '}
          {importMutation.data.recharges} wallet top-up(s), {importMutation.data.unmatched}{' '}
          unmatched.
          {importMutation.data.balanceBreaks.length > 0 &&
            ` ${importMutation.data.balanceBreaks.length} running-balance break(s) — the export may be missing lines.`}
        </div>
      )}
      {importMutation.error && (
        <p role="alert" className="text-[12px] font-bold text-red-700">
          {errorText(importMutation.error)}
        </p>
      )}
    </div>
  );
}

function ResolveBooking({
  row,
  onDone,
  kind = 'shipment',
}: {
  row:
    | CourierExceptions['ambiguousBookings'][number]
    | CourierExceptions['ambiguousReturnBookings'][number];
  onDone: () => void;
  kind?: 'shipment' | 'return';
}) {
  const creating = !row.providerShipmentId;
  const [awb, setAwb] = useState('');
  const [orderId, setOrderId] = useState('');
  const [shipmentId, setShipmentId] = useState('');
  const resolve = useMutation({
    mutationFn: (body: Parameters<typeof api.courierFinance.resolveBooking>[1]) =>
      kind === 'return'
        ? api.courierFinance.resolveReturnBooking(row.id, body)
        : api.courierFinance.resolveBooking(row.id, body),
    onSuccess: onDone,
  });
  return (
    <div className="border-border/40 space-y-2 border-t py-2 text-[12px]">
      <p className="text-ink">
        <span className="font-mono">
          {'subOrderId' in row ? row.subOrderId : `return ${row.returnRequestId}`}
        </span>{' '}
        — {creating ? 'creating the Shiprocket order' : 'assigning the AWB'} got no answer.{' '}
        <span className="text-ink-subtle">{row.bookingError}</span>
      </p>
      <p className="text-ink-subtle">Check the Shiprocket panel, then record what you find.</p>
      {creating ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            placeholder="Shiprocket order id"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            className="border-border/60 rounded-lg border px-2 py-1"
          />
          <input
            placeholder="Shiprocket shipment id"
            value={shipmentId}
            onChange={(e) => setShipmentId(e.target.value)}
            className="border-border/60 rounded-lg border px-2 py-1"
          />
          <button
            type="button"
            className={btn}
            disabled={!orderId || !shipmentId || resolve.isPending}
            onClick={() =>
              resolve.mutate({
                outcome: 'CREATED',
                providerOrderId: orderId,
                providerShipmentId: shipmentId,
              })
            }
          >
            It exists
          </button>
          <button
            type="button"
            className={btn}
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ outcome: 'NOT_CREATED' })}
          >
            It was not created
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <input
            placeholder="AWB issued"
            value={awb}
            onChange={(e) => setAwb(e.target.value)}
            className="border-border/60 rounded-lg border px-2 py-1"
          />
          <button
            type="button"
            className={btn}
            disabled={awb.trim().length < 4 || resolve.isPending}
            onClick={() => resolve.mutate({ outcome: 'BOOKED', awbNumber: awb.trim() })}
          >
            It was booked
          </button>
          <button
            type="button"
            className={btn}
            disabled={resolve.isPending}
            onClick={() => resolve.mutate({ outcome: 'NOT_BOOKED' })}
          >
            No AWB was issued
          </button>
        </div>
      )}
      {resolve.error && <p className="font-bold text-red-700">{errorText(resolve.error)}</p>}
    </div>
  );
}

export function CourierMoneyTab() {
  const qc = useQueryClient();
  const exceptions = useQuery({
    queryKey: ['courier-exceptions'],
    queryFn: () => api.courierFinance.exceptions(),
  });
  const dead = useQuery({
    queryKey: ['finance-outbox', 'DEAD'],
    queryFn: () => api.courierFinance.outbox({ status: 'DEAD' }),
  });
  const [legacy, setLegacy] = useState<Record<string, unknown> | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['courier-exceptions'] });
    void qc.invalidateQueries({ queryKey: ['finance-outbox'] });
  };
  const rematch = useMutation({
    mutationFn: () => api.courierFinance.rematch(),
    onSuccess: refresh,
  });
  const ignore = useMutation({
    mutationFn: (id: string) => api.courierFinance.ignoreWalletTxn(id, 'Not a shipment cost'),
    onSuccess: refresh,
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.courierFinance.retryOutbox(id),
    onSuccess: refresh,
  });
  const legacyRun = useMutation({
    mutationFn: () => api.courierFinance.legacyReport(),
    onSuccess: setLegacy,
  });

  if (exceptions.isLoading) return <Spinner />;
  if (exceptions.error)
    return <p className="text-[13px] font-bold text-red-700">{errorText(exceptions.error)}</p>;
  const x = exceptions.data!;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          ['Awaiting courier evidence', x.counts.chargesAwaitingEvidence],
          ['AWBs with no cost recorded', x.counts.awbWithoutCharge],
          ['Unmatched passbook lines', x.counts.unmatchedWalletLines],
          ['Bookings to resolve', x.counts.ambiguousBookings],
        ].map(([label, n]) => (
          <div key={label as string} className="border-border/60 bg-surface rounded-2xl border p-4">
            <p className="text-ink-subtle text-[11px] font-bold uppercase">{label}</p>
            <p className="text-ink mt-1 text-[20px] font-black">{n as number}</p>
          </div>
        ))}
        <div className="border-border/60 bg-surface rounded-2xl border p-4">
          <p className="text-ink-subtle text-[11px] font-bold uppercase">Wallet (per passbook)</p>
          <p className="text-ink mt-1 text-[20px] font-black">
            {inr(x.walletBalance?.balancePaise)}
          </p>
          <p className="text-ink-subtle text-[11px]">
            {x.walletBalance
              ? `as of ${new Date(x.walletBalance.asOf).toLocaleString('en-IN')} — not live`
              : 'No passbook imported yet'}
          </p>
        </div>
      </div>

      <Section
        title="Import Shiprocket passbook"
        hint="Download the wallet passbook CSV from the Shiprocket panel and upload it here. Each line is matched to its shipment by AWB."
      >
        <PassbookImport onDone={refresh} />
      </Section>

      <Section
        title="Bookings with an unknown outcome"
        count={x.ambiguousBookings.length + x.ambiguousReturnBookings.length}
        hint="Shiprocket did not answer. Nothing is booked again until you record what happened."
      >
        {x.ambiguousBookings.length + x.ambiguousReturnBookings.length === 0 ? (
          <p className="text-ink-subtle text-[12px]">Nothing here.</p>
        ) : (
          <>
            {x.ambiguousBookings.map((b) => (
              <ResolveBooking key={b.id} row={b} onDone={refresh} />
            ))}
            {x.ambiguousReturnBookings.map((b) => (
              <ResolveBooking key={b.id} row={b} kind="return" onDone={refresh} />
            ))}
          </>
        )}
      </Section>

      <Section title="Unmatched passbook lines" count={x.unmatchedWallet.length}>
        <div className="mb-2">
          <button
            type="button"
            className={btn}
            onClick={() => rematch.mutate()}
            disabled={rematch.isPending}
          >
            Match again
          </button>
        </div>
        {x.unmatchedWallet.length === 0 ? (
          <p className="text-ink-subtle text-[12px]">Nothing here.</p>
        ) : (
          <table className="w-full text-left text-[12px]">
            <tbody>
              {x.unmatchedWallet.map((t) => (
                <tr key={t.id} className="border-border/40 border-t">
                  <td className="py-1.5 pr-3">
                    {new Date(t.occurredAt).toLocaleDateString('en-IN')}
                  </td>
                  <td className="py-1.5 pr-3 font-mono">{t.awbNumber ?? '—'}</td>
                  <td className="py-1.5 pr-3">{t.description}</td>
                  <td className="py-1.5 pr-3 text-right">{inr(t.amountPaise)}</td>
                  <td className="py-1.5">
                    <button type="button" className={btn} onClick={() => ignore.mutate(t.id)}>
                      Not a shipment cost
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Costs whose amount is still unknown"
        count={x.pendingEvidence.length}
        hint="Recorded when the cost was incurred (reverse pickups, bookings without a usable quote). Filled in by the passbook."
      >
        <ChargeTable rows={x.pendingEvidence} />
      </Section>

      <Section
        title="Cancelled bookings awaiting a wallet credit"
        count={x.cancelledAwaitingCredit.length}
        hint="Whether Shiprocket refunds a cancelled AWB is only known once the credit appears on the passbook."
      >
        <ChargeTable rows={x.cancelledAwaitingCredit} />
      </Section>

      <Section title="Estimates older than 14 days" count={x.staleEstimates.length}>
        <ChargeTable rows={x.staleEstimates} />
      </Section>

      <Section
        title="Seller-borne costs on cancelled packages"
        count={x.unrecoveredOnClosed.length}
        hint="These packages will never be settled, so nothing charges the seller automatically."
      >
        <ChargeTable rows={x.unrecoveredOnClosed} />
      </Section>

      <Section
        title="Delivery charged below what the courier billed"
        count={x.shippingShortfalls.length}
      >
        {x.shippingShortfalls.length === 0 ? (
          <p className="text-ink-subtle text-[12px]">Nothing here.</p>
        ) : (
          <table className="w-full text-left text-[12px]">
            <tbody>
              {x.shippingShortfalls.map((s) => (
                <tr key={s.subOrderId} className="border-border/40 border-t">
                  <td className="py-1.5 pr-3 font-mono">{s.subOrderId}</td>
                  <td className="py-1.5 pr-3">customer paid {inr(s.customerPaise)}</td>
                  <td className="py-1.5 pr-3">courier billed {inr(s.actualPaise)}</td>
                  <td className="py-1.5 text-right font-bold">
                    <Badge tone="warning">{inr(s.actualPaise - s.customerPaise)}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="AWBs with no cost recorded"
        count={x.awbWithoutCharge.length}
        hint="Usually bookings made before courier costs were recorded."
      >
        {x.awbWithoutCharge.length === 0 ? (
          <p className="text-ink-subtle text-[12px]">Nothing here.</p>
        ) : (
          <ul className="space-y-1 text-[12px]">
            {x.awbWithoutCharge.map((s) => (
              <li key={s.id} className="font-mono">
                {s.awbNumber} · {s.status} · quote on booking {inr(s.freightChargePaise)}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Courier cancellations that failed" count={x.failedCancellations.length}>
        {x.failedCancellations.length === 0 ? (
          <p className="text-ink-subtle text-[12px]">Nothing here.</p>
        ) : (
          <ul className="space-y-1 text-[12px]">
            {x.failedCancellations.map((s) => (
              <li key={s.id}>
                <span className="font-mono">{s.awbNumber ?? s.subOrderId}</span> —{' '}
                {s.cancellationError}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Financial follow-ups that keep failing"
        count={dead.data?.page.total ?? 0}
        hint="Refunds, courier charges and settlement clawbacks the system retried and gave up on. Fix the cause, then retry."
      >
        {(dead.data?.data ?? []).length === 0 ? (
          <p className="text-ink-subtle text-[12px]">Nothing here.</p>
        ) : (
          <ul className="space-y-2 text-[12px]">
            {dead.data!.data.map((o) => (
              <li key={o.id} className="flex items-start justify-between gap-3">
                <span>
                  <span className="font-mono">{o.kind}</span> · {o.attempts} attempts ·{' '}
                  <span className="text-red-700">{o.lastError}</span>
                </span>
                <button type="button" className={btn} onClick={() => retry.mutate(o.id)}>
                  Retry
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Legacy data report"
        hint="Read-only counts of historical records whose money cannot be reconciled from what is stored."
      >
        <button
          type="button"
          className={btn}
          onClick={() => legacyRun.mutate()}
          disabled={legacyRun.isPending}
        >
          {legacyRun.isPending ? 'Running…' : 'Run report'}
        </button>
        {legacy && (
          <pre className="bg-surface-subtle mt-3 overflow-x-auto rounded-lg p-3 text-[11px]">
            {JSON.stringify(legacy, null, 2)}
          </pre>
        )}
      </Section>
    </div>
  );
}
